import React, { Component } from "react";
import { FormattedMessage, type IntlShape, injectIntl } from "react-intl";
import { type ConnectedProps, connect } from "react-redux";
import { Redirect, type RouteComponentProps } from "react-router-dom";

import { eraseStateProp } from "~/actions/dashboard";
import { getPatientId } from "~/actions/get_patient_id";
import { orderPowerCap, type TPowerCapShipment } from "~/actions/order_power_cap";
import { SOArch, SOMaterial } from "~/common/constants";
import { remoteLog } from "~/common/logging";
import { getLastCorrection, isPatient } from "~/common/patient";
import { RetainersQuantityGroup } from "~/components/3d_plan_retainers/3d_plan_retainers_page";
import { Label } from "~/components/ui/label";
import { LabelledCheckbox } from "~/components/ui/labelled-checkbox";
import { LabelledRadio, LabelledRadioGroup } from "~/components/ui/labelled-radio";
import { Layout } from "~/components/ui/layout";
import { Loader } from "~/components/ui/loader";
import { LoadingButton } from "~/components/ui/loading-button";
import { Portlet, PortletTitle } from "~/components/ui/portlet";
import { TextArea } from "~/components/ui/textarea";
import { setDocumentTitle } from "~/hooks/use-document-title";
import type { RootState } from "~/store";

import PowerCapFileUpload from "./power_cap_file_upload";

const mapStateToProps = (state: RootState) => ({
  patient: state.patient,
  media: state.media,
  media_s3: state.media_s3,
});

const mapDispatchToProps = {
  eraseStateProp,
  getPatientId,
  orderPowerCap,
};

type PlanPowerCapProps = PropsFromRedux & { intl: IntlShape } & RouteComponentProps<{
    patient_id: string;
  }> & {
    bareLayout?: boolean;
  };

type PlanPowerCapState = {
  isSubmitting: boolean;
  submitted: boolean;
  arch: SOArch | null;
  comment: string;
  shipWithRetainers: boolean;
  orderRetainers: boolean;
  retainersArch: SOArch | null;
  retainersQuantity: number | null;
};

const ARCH_OPTIONS = [
  [SOArch.BOTH, "TA_BOTH"],
  [SOArch.UPPER, "TA_UPPER"],
  [SOArch.LOWER, "TA_LOWER"],
] as const;

class PlanPowerCap extends Component<PlanPowerCapProps, PlanPowerCapState> {
  constructor(props: PlanPowerCapProps) {
    super(props);
    this.state = {
      isSubmitting: false,
      submitted: false,
      arch: null,
      comment: "",
      shipWithRetainers: true,
      orderRetainers: false,
      retainersArch: null,
      retainersQuantity: null,
    };
  }

  componentDidCatch(e: Error) {
    remoteLog(e, "3d_plan_power_cap_page_body");
  }

  componentDidMount() {
    this.props.eraseStateProp("media");
    const { patient_id } = this.props.match.params;
    void this.props.getPatientId(Number(patient_id));
    setDocumentTitle(this.props.intl.formatMessage({ id: "pat.power_cap.page.header" }), patient_id);
  }

  /** Infer scan use from an uploaded STL; all other uploads use the impressions path. */
  detectMaterial(): SOMaterial {
    const isScanUploaded = Object.entries(this.props.media).some(([_, { user_filename }]) => user_filename?.endsWith(".stl"));
    return isScanUploaded ? SOMaterial.USE_SCAN : SOMaterial.USE_IMPRESSIONS;
  }

  async orderPowerCapSubmit() {
    const { patient_id } = this.props.match.params;
    const { arch, comment } = this.state;
    const validity = this.validateRequiredFields();

    $("#validation-files").css({ color: validity.files ? "#34495e" : "red" });
    $("#validation-arch").css({ color: validity.arch ? "#34495e" : "red" });
    $("#validation-power-cap-comment").css({ color: validity.comment ? "#34495e" : "red" });
    $("#validation-retainers-arch").css({ color: validity.retainersArch ? "#34495e" : "red" });
    $("#validation-quantity").css({ color: validity.retainersQuantity ? "#34495e" : "red" });

    if (Object.values(validity).some((v) => v == false)) {
      return;
    }

    this.setState({ isSubmitting: true });
    try {
      if (arch) {
        const material = this.detectMaterial();
        // BUG FIX 2026-09-21 ("STL через существующего пациента не загрузилась"): a power-cap
        // order creates AND submits its child заявка atomically on the backend (Doctor
        // .order_power_cap) — the child is already past `UNFILLED_CASE` the instant this call
        // returns, so there is never a draft window afterward in which a separate `updateMedia`
        // PUT could attach the doctor's uploaded files (that generic patient-edit endpoint
        // rejects a media-only update once a case has moved past `UNFILLED_CASE`). Sending the
        // deferred-upload media straight through THIS call instead — matching patient_new.tsx's
        // own createPatient(), which always sends whatever is in `media`/`media_s3` too — lets
        // the backend attach it in the same transaction that creates the child, so it can never
        // again land "attached to nothing".
        await this.props.orderPowerCap(Number(patient_id), material, arch, comment.trim(), this.props.media, this.props.media_s3, this.shipment());
        this.setState({ submitted: true });
      }
    } finally {
      this.setState({ isSubmitting: false });
    }
  }

  shipmentOptions() {
    const { patient } = this.props;
    if (!isPatient(patient)) {
      return { canShipWithRetainers: false, canOrderRetainers: false };
    }
    const { can_add_power_cap_to_retainers, can_order_retainers } = getLastCorrection(patient).order_options;
    if (typeof can_add_power_cap_to_retainers !== "boolean") {
      return { canShipWithRetainers: false, canOrderRetainers: false };
    }
    return {
      canShipWithRetainers: can_add_power_cap_to_retainers,
      canOrderRetainers: !can_add_power_cap_to_retainers && can_order_retainers,
    };
  }

  shipment(): TPowerCapShipment {
    const { shipWithRetainers, orderRetainers, retainersArch, retainersQuantity } = this.state;
    const { canShipWithRetainers, canOrderRetainers } = this.shipmentOptions();
    if (canShipWithRetainers && shipWithRetainers) {
      return { ship_with_retainers: true };
    }
    if (canOrderRetainers && orderRetainers && retainersArch != null && retainersQuantity != null) {
      return { retainers: { treat_arches_id: retainersArch, quantity: retainersQuantity } };
    }
    return null;
  }

  validateRequiredFields() {
    const { arch, comment, orderRetainers, retainersArch, retainersQuantity } = this.state;
    const hasFiles = Object.keys(this.props.media).length > 0;
    const needsRetainers = this.shipmentOptions().canOrderRetainers && orderRetainers;
    return {
      files: hasFiles,
      arch: arch != null,
      comment: comment.trim().length > 0,
      retainersArch: !needsRetainers || retainersArch != null,
      retainersQuantity: !needsRetainers || retainersQuantity != null,
    };
  }

  render() {
    const { patient } = this.props;
    const { isSubmitting, arch, comment, shipWithRetainers, orderRetainers, retainersArch, retainersQuantity } = this.state;
    const Chrome = this.props.bareLayout ? React.Fragment : Layout;

    if (!isPatient(patient)) {
      return (
        <Chrome>
          <Loader />
        </Chrome>
      );
    }

    if (this.state.submitted) {
      return <Redirect to={`/pages/patient/${patient.patient_id}`} />;
    }

    const { can_order_power_cap } = getLastCorrection(patient).order_options;
    if (!can_order_power_cap) {
      return <Redirect to={`/pages/patient/${patient.patient_id}`} />;
    }
    const { canShipWithRetainers, canOrderRetainers } = this.shipmentOptions();

    return (
      <Chrome>
        <Portlet as="main" id="power-cap-section-head">
          <PortletTitle iconClassName="icon-book-open" displayRequiredFieldsInfo>
            <FormattedMessage id="pat.power_cap.page.header" />
          </PortletTitle>

          <div className="portlet-body">
            <form
              className="tw-space-y-5"
              onSubmit={(event) => {
                event.preventDefault();
                void this.orderPowerCapSubmit();
              }}
            >
              <h4 id="arches-heading" className="block">
                <FormattedMessage id="HEADER_ARCHES" />
              </h4>

              <LabelledRadioGroup label={<FormattedMessage id="pat.power_cap.arches_select" />} labelProps={{ id: "validation-arch", size: "xl" }} required>
                {ARCH_OPTIONS.map(([value, intlId]) => (
                  <LabelledRadio key={value} label={<FormattedMessage id={intlId} />} name="treat_arches_id" value={value} checked={value == arch} onChange={() => this.setState({ arch: value })} />
                ))}
              </LabelledRadioGroup>

              <PowerCapFileUpload required isVisible />

              <div className="form-group">
                <Label id="validation-power-cap-comment" htmlFor="power-cap-comment" required>
                  <FormattedMessage id="pat.power_cap.comment" />
                </Label>
                <TextArea id="power-cap-comment" rows={5} value={comment} onChange={(event) => this.setState({ comment: event.target.value })} />
              </div>

              {canShipWithRetainers ? (
                <LabelledCheckbox
                  id="power-cap-ship-with-retainers"
                  label={<FormattedMessage id="pat.power_cap.ship_with_retainers" />}
                  checked={shipWithRetainers}
                  onChange={(event) => this.setState({ shipWithRetainers: event.target.checked })}
                />
              ) : null}

              {canOrderRetainers ? (
                <div className="tw-space-y-5">
                  <LabelledCheckbox
                    id="power-cap-order-retainers"
                    label={<FormattedMessage id="pat.power_cap.order_retainers" />}
                    checked={orderRetainers}
                    onChange={(event) => this.setState({ orderRetainers: event.target.checked })}
                  />
                  {orderRetainers ? (
                    <>
                      <LabelledRadioGroup label={<FormattedMessage id="ARCHES_SELECT" />} labelProps={{ id: "validation-retainers-arch" }} required>
                        {ARCH_OPTIONS.map(([value, intlId]) => (
                          <LabelledRadio
                            key={value}
                            label={<FormattedMessage id={intlId} />}
                            name="retainers_treat_arches_id"
                            value={value}
                            checked={value == retainersArch}
                            onChange={() => this.setState({ retainersArch: value })}
                          />
                        ))}
                      </LabelledRadioGroup>
                      <RetainersQuantityGroup quantity={retainersQuantity} onQuantityChange={(newQuantity) => this.setState({ retainersQuantity: newQuantity })} />
                    </>
                  ) : null}
                </div>
              ) : null}

              <LoadingButton id="submit-power-cap-btn" variant="primary" size="xl" isLoading={isSubmitting}>
                <FormattedMessage id="pat.power_cap.buttons.submit" />
              </LoadingButton>
            </form>
          </div>
        </Portlet>
      </Chrome>
    );
  }
}

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(injectIntl(PlanPowerCap));
